import { GraphModel } from './graph.model';
import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';

type Params = Record<string, never>;

export class ClearChangesAction extends BaseAction<Params, { t: string }[]> {
  title = 'Clear Changes';
  description = 'Clear all unpublished changes from local storage';
  inputSchema = z.object({});
  group = GRAPH_ACTION_GROUP;
  icon = 'trash';

  applicable(context: ApplicableContext): boolean {
    const graph = context.getInScopeModelOfType(GraphModel);

    if (graph === null) {
      return false;
    }

    // Only show action if there are unpublished operations
    return graph.hasUnpublishedOperations.snapshot();
  }

  async execute(
    _payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(graph !== null, 'Graph model not found');

    const operations = graph.stagedOperations.snapshot();

    if (operations.length === 0) {
      toast.info('No changes to clear');
      return ok({});
    }

    const clearToast = toast.loading('Clearing changes...');

    try {
      // Clear staged operations from both cache and storage
      graph.stagedOperations.set([]);
      await graph.userBranches.clearStagedOperations();

      clearToast.success(
        `Cleared ${operations.length} unpublished change${
          operations.length === 1 ? '' : 's'
        }`
      );
      return ok({});
    } catch (error: any) {
      // eslint-disable-next-line no-console
      clearToast.error('Failed to clear changes. Please try again.');
      return err([
        { t: 'error', message: 'Failed to clear changes. Please try again.' },
      ]);
    }
  }
}
